import React, { useRef, useMemo } from 'react';
import { Transition } from 'react-transition-group';
import CardBenefitPartner from '../../../CardBenefitPartner/CardBenefitPartner';
import { BenefitLoader } from '../../../skeleton-loaders/BenefitLoader';
import { CustomBenefitListBlockType } from '../../../../../../types/customBenefitListBlockType';
import { getPlaceholderList } from '../../../../utils/getPlaceholderList';
import * as S from './CustomBenefitListBlock.styles';

const CustomBenefitListBlock = ({ block }: { block: CustomBenefitListBlockType }) => {
  const { title, isLoading = false, data } = block;
  const placeholderGridRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);

  // If there's an active campaign, we want to show the campaign data in the listing.
  const processedBenefits = useMemo(
    () =>
      (data ?? []).map((benefit) => {
        const activeCampaign = benefit.campaignDetails?.campaigns?.[0];

        if (activeCampaign) {
          return {
            ...benefit,
            image: activeCampaign.image,
            listIngress: activeCampaign.listIngress,
            name: activeCampaign.listTitle,
            title: activeCampaign.listTitle,
            discountBadge: activeCampaign.listDiscountBadge,
          };
        }

        return benefit;
      }),
    [data],
  );

  return (
    <S.Container>
      {title && <S.Title>{title}</S.Title>}
      <S.AnimationGrid>
        {isLoading && (
          <Transition
            nodeRef={placeholderGridRef}
            in={isLoading}
            timeout={{ enter: 250, exit: 0 }}
            unmountOnExit
            mountOnEnter
          >
            {(state) => (
              <S.StyledAnimationWrapper ref={placeholderGridRef} $state={state}>
                {getPlaceholderList(12).map((i) => (
                  <BenefitLoader key={`benefit-placeholder-item-${i}`} />
                ))}
              </S.StyledAnimationWrapper>
            )}
          </Transition>
        )}
        {!isLoading && (
          <Transition nodeRef={gridRef} key="data" in={!isLoading} timeout={250} unmountOnExit mountOnEnter>
            {(state) => (
              <S.StyledAnimationWrapper ref={gridRef} $state={state}>
                {processedBenefits.length > 0
                  ? processedBenefits.map((item) => (
                      <CardBenefitPartner isFlexible benefit={item} key={`item-${item.slug || item.id}`} />
                    ))
                  : null}
              </S.StyledAnimationWrapper>
            )}
          </Transition>
        )}
      </S.AnimationGrid>
    </S.Container>
  );
};

export default CustomBenefitListBlock;
